import { computed, Injectable, signal } from '@angular/core';
import { IServico, ITipoServico } from '@nomoh-monorepo/shared-types';

/**
 * Data Service para TipoServico - Gestão de estado com Signals
 */
@Injectable({
  providedIn: 'root',
})
export class TipoServicoDataService {
  // State privado
  private _tiposServico = signal<ITipoServico[]>([]);
  private _tipoServicoAtual = signal<ITipoServico | null>(null);
  private _loading = signal(false);
  private _erro = signal<string | null>(null);

  // Signals públicos (readonly)
  $tiposServico = this._tiposServico.asReadonly();
  $tipoServicoAtual = this._tipoServicoAtual.asReadonly();
  $loading = this._loading.asReadonly();
  $erro = this._erro.asReadonly();

  // Computed signals
  $tiposServicoAtivos = computed(() =>
    this._tiposServico().filter((t) => t.status === true)
  );

  $totalTiposServico = computed(() => this._tiposServico().length);

  // Setters
  setTiposServico(tiposServico: ITipoServico[]) {
    this._tiposServico.set(this.normalizarTiposServico(tiposServico));
  }

  setTipoServicoAtual(tipoServico: ITipoServico | null) {
    this._tipoServicoAtual.set(tipoServico);
  }

  addTipoServico(tipoServico: ITipoServico) {
    this._tiposServico.update((current) =>
      this.normalizarTiposServico([...current, tipoServico])
    );
  }

  updateTipoServico(id: string, data: Partial<ITipoServico>) {
    this._tiposServico.update((current) =>
      this.normalizarTiposServico(
        current.map((t) => (t.id === id ? { ...t, ...data } : t))
      )
    );

    if (this._tipoServicoAtual()?.id === id) {
      this._tipoServicoAtual.update((current) =>
        current ? { ...current, ...data } : null
      );
    }
  }

  removeTipoServico(id: string) {
    this._tiposServico.update((current) =>
      this.normalizarTiposServico(current.filter((t) => t.id !== id))
    );

    if (this._tipoServicoAtual()?.id === id) {
      this._tipoServicoAtual.set(null);
    }
  }

  upsertServico(servico: IServico) {
    if (!servico?.id) {
      return;
    }

    const deveRealocar = servico.tipoServicoId !== undefined;

    this._tiposServico.update((current) => {
      const tipoDestinoId =
        servico.tipoServicoId ??
        current.find((tipo) =>
          (
            (tipo as ITipoServico & { servicos?: IServico[] }).servicos ?? []
          ).some((item) => String(item.id) === String(servico.id))
        )?.id;

      if (!tipoDestinoId) {
        return current;
      }

      return this.normalizarTiposServico(
        current.map((tipo) => {
          const servicos = (
            tipo as ITipoServico & {
              servicos?: IServico[];
            }
          ).servicos;

          if (!Array.isArray(servicos)) {
            return tipo;
          }

          const pertenceAoTipo = String(tipo.id) === String(tipoDestinoId);
          const servicosSemAtual = servicos.filter(
            (item) => String(item.id) !== String(servico.id)
          );

          if (!pertenceAoTipo) {
            if (!deveRealocar) {
              return tipo;
            }

            return servicosSemAtual.length === servicos.length
              ? tipo
              : ({
                  ...tipo,
                  servicos: servicosSemAtual,
                } as ITipoServico & { servicos?: IServico[] });
          }

          return {
            ...tipo,
            servicos: [...servicosSemAtual, servico],
          } as ITipoServico & { servicos?: IServico[] };
        })
      );
    });
  }

  removeServico(servicoId: string) {
    if (!servicoId) {
      return;
    }

    this._tiposServico.update((current) =>
      this.normalizarTiposServico(
        current.map((tipo) => {
          const servicos = (
            tipo as ITipoServico & {
              servicos?: IServico[];
            }
          ).servicos;

          if (!Array.isArray(servicos)) {
            return tipo;
          }

          const filtrados = servicos.filter(
            (item) => String(item.id) !== String(servicoId)
          );

          return filtrados.length === servicos.length
            ? tipo
            : ({
                ...tipo,
                servicos: filtrados,
              } as ITipoServico & { servicos?: IServico[] });
        })
      )
    );
  }

  setLoading(loading: boolean) {
    this._loading.set(loading);
  }

  setErro(erro: string | null) {
    this._erro.set(erro);
  }

  limparErro() {
    this._erro.set(null);
  }

  limpar() {
    this._tiposServico.set([]);
    this._tipoServicoAtual.set(null);
    this._loading.set(false);
    this._erro.set(null);
  }

  private normalizarTiposServico(tiposServico: ITipoServico[]): ITipoServico[] {
    const tipos = (tiposServico || []).map((tipo) => {
      const servicos = (
        tipo as ITipoServico & {
          servicos?: IServico[];
        }
      ).servicos;

      if (!Array.isArray(servicos)) {
        return tipo;
      }

      const servicosOrdenados = [...servicos].sort((a, b) =>
        a.nome.localeCompare(b.nome)
      );

      return {
        ...tipo,
        servicos: servicosOrdenados,
      } as ITipoServico & { servicos?: IServico[] };
    });

    return [...tipos].sort((a, b) => a.nome.localeCompare(b.nome));
  }
}
